import { Loading } from '@tcc-online/common-components';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { useTranslate } from 'react-translate.ts';

import ProfileActions from '../../actions/ProfileActions';
import BasePageLayout from '../../common/components/BasePageLayout/BasePageLayout';
import UserCard from '../../components/cards/UserCard/UserCard';
import { FORBIDDEN_PATH } from '../../constants/route.constants';
import { AppState } from '../../reducers/root.reducer';
import AuthSelectors from '../../selectors/AuthSelectors';
import ProfileSelectors from '../../selectors/ProfileSelectors';
import { UserService } from '../../services/common/UserService';
import { IUser } from '../../types';
import { TRANSLATIONS_ROOT_KEY } from '../../constants/translation.constants';
import { AuthService } from '../../services/AuthService';
import UserRoles from '../../common/constants/UserRoles';

export default function ProfilePage() {
    const dispatch = useDispatch();
    const translate = useTranslate(TRANSLATIONS_ROOT_KEY);
    const { profile } = useSelector(mapState);

    const userId = UserService.getUserId();
    const isAnonymous = useSelector(AuthSelectors.isAnonymous);
    const accessToken = useSelector(AuthSelectors.getAuthToken);

    const isAdmin = AuthService.hasUserAnyOfRoles(accessToken, [
        UserRoles.manager,
        UserRoles.sysAdmin,
        UserRoles.admin,
    ]);

    useEffect(() => {
        if (!userId) {
            return;
        }

        dispatch(ProfileActions.getProfile(userId));
    }, [dispatch, userId]);

    if (!profile) {
        return <Loading isLoading isTransparent={false} />;
    }

    if (isAnonymous === true) {
        return <Navigate to={FORBIDDEN_PATH} relative="path" />;
    }

    return (
        <BasePageLayout heading={translate('nav.user.profile')}>
            <UserCard
                user={profile}
                onSubmit={submit}
                selfEdit
                isAdmin={isAdmin}
            />
        </BasePageLayout>
    );

    function submit(values: Partial<IUser>) {
        const { firstName, lastName } = values;

        dispatch(ProfileActions.saveProfile(userId, firstName, lastName));
    }
}

function mapState(state: AppState) {
    return {
        profile: ProfileSelectors.getProfile(state),
    };
}
